import React, { useCallback, useEffect, useRef } from 'react';
import { BlockerFunction, useBlocker, useLocation, useNavigate } from 'react-router';
import { alertAssignmentInternalApi, ApiSortDirection, FindAlertFilterResultsOrderByEnum } from '../../apis';
import { SalvButton, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import { faChevronLeft, faChevronRight, faList } from '@fortawesome/pro-solid-svg-icons';
import { assignAndGoToNextScreeningAlertInFilter } from '../../screening/alert/filters/screeningAlertFiltersActions';
import { assignAndGoToNextAlertInFilter } from '../filters/filteredAlertsActions';
import {
  AssignNextAlertFromFilterOrderByEnum,
  InternalApiAlertType,
  InternalApiSortDirection,
} from '../../api-internal';
import useShortcut from '../../common/useShortcut';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';

export const useAlertFlow = (assignedToSelf: boolean, alertId: number, alertType: InternalApiAlertType) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const isScreeningAlertFlow = searchParams.get('flow') === 'true';
  const isAlertFlow = searchParams.get('alertFlow') === 'true';
  const filterId = searchParams.get('filterId');
  const sortDirection: ApiSortDirection =
    searchParams.get('direction') === ApiSortDirection.ASC ? ApiSortDirection.ASC : ApiSortDirection.DESC;
  const sortBy: AssignNextAlertFromFilterOrderByEnum =
    searchParams.get('sortBy') === FindAlertFilterResultsOrderByEnum.STATUS_UPDATED_TIME
      ? AssignNextAlertFromFilterOrderByEnum.STATUS_UPDATED_TIME
      : AssignNextAlertFromFilterOrderByEnum.CREATED_TIME;
  const offset = searchParams.get('offset') ? new Date(searchParams.get('offset')!) : undefined;

  const alertIsDirty = useRef(false);

  const shouldBlock: BlockerFunction = useCallback(
    ({ nextLocation }) => {
      return (
        (isScreeningAlertFlow || isAlertFlow) &&
        assignedToSelf &&
        !alertIsDirty.current &&
        !nextLocation.pathname.includes('login') &&
        !nextLocation.pathname.includes('customer') &&
        !nextLocation.pathname.includes('transactions')
      );
    },
    [alertIsDirty.current, isAlertFlow, isScreeningAlertFlow, assignedToSelf]
  );

  const blocker = useBlocker(shouldBlock);

  const setDirty = useCallback(() => {
    alertIsDirty.current = true;
  }, [alertIsDirty]);

  const resetBlocker = useCallback(() => {
    blocker.state === 'blocked' && blocker.reset();
    alertIsDirty.current = false;
  }, [blocker, alertIsDirty]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description:
            "This alert was assigned to you, but you haven't made any changes to it. Do you want to keep it" +
            ' assigned to you?',
          confirmationText: 'Yes',
          cancelText: 'No, unassign it',
          onCancel: async () => {
            await alertAssignmentInternalApi.unassignAlert({
              internalApiAlertAssignmentCommand: {
                alertId,
                alertType,
              },
            });
          },
        })
      ).then(() => {
        blocker.proceed();
      });
    }
  }, [blocker.state, alertId, alertType]);

  const navigateToNextAlert = useCallback(() => {
    if (!filterId) {
      return;
    } else if (isAlertFlow) {
      dispatch(
        assignAndGoToNextAlertInFilter(
          Number.parseInt(filterId),
          sortDirection as unknown as InternalApiSortDirection,
          sortBy,
          navigate,
          offset,
          alertId,
          alertType
        )
      );
    } else if (isScreeningAlertFlow) {
      dispatch(assignAndGoToNextScreeningAlertInFilter(Number.parseInt(filterId), sortDirection, navigate, alertId));
    }
  }, [
    dispatch,
    filterId,
    isAlertFlow,
    isScreeningAlertFlow,
    sortDirection,
    sortBy,
    navigate,
    offset,
    alertId,
    alertType,
  ]);
  const navigateToPreviousAlert = useCallback(() => {
    if (!filterId) {
      return;
    } else if (isAlertFlow || isScreeningAlertFlow) {
      navigate(-1);
    }
  }, [navigate, isAlertFlow, isScreeningAlertFlow, filterId]);
  useShortcut({ triggerKey: '.', onTrigger: navigateToNextAlert });
  useShortcut({ triggerKey: ',', onTrigger: navigateToPreviousAlert });

  const renderNavigationButtons = () => {
    if (!isScreeningAlertFlow && !isAlertFlow) {
      return <></>;
    }
    return (
      <>
        <SalvButton
          variant='muted'
          size={Size.SMALL}
          className='flow-navigation-button'
          label='Back to list view'
          iconLeft={faList}
          onClick={() => {
            const alertViewUrl = isAlertFlow
              ? `/alert-filters?id=${filterId}&direction=${sortDirection}&sortBy=${sortBy}&offset=${offset}`
              : `/screening/alert-filters?id=${filterId}&direction=${sortDirection}`;
            navigate(alertViewUrl);
          }}
        />
        <SalvTooltipWrapper tooltip='Previous alert' shortcut=','>
          <SalvButton
            variant='muted'
            size={Size.SMALL}
            className='flow-navigation-button'
            ariaLabel='Previous alert'
            iconLeft={faChevronLeft}
            onClick={navigateToPreviousAlert}
          />
        </SalvTooltipWrapper>
        {isScreeningAlertFlow && (
          <SalvTooltipWrapper tooltip='Next alert' shortcut='.' placement={'bottom right'}>
            <SalvButton
              variant='muted'
              size={Size.SMALL}
              className='flow-navigation-button'
              ariaLabel='Next alert'
              iconLeft={faChevronRight}
              onClick={navigateToNextAlert}
            />
          </SalvTooltipWrapper>
        )}
        {isAlertFlow && (
          <SalvTooltipWrapper tooltip='Next alert' shortcut='.' placement={'bottom right'}>
            <SalvButton
              variant='muted'
              size={Size.SMALL}
              className='flow-navigation-button'
              iconLeft={faChevronRight}
              ariaLabel='Next alert'
              onClick={navigateToNextAlert}
            />
          </SalvTooltipWrapper>
        )}
      </>
    );
  };

  return [setDirty, resetBlocker, renderNavigationButtons] as const;
};
